import {
  Box,
  Button,
  Collapse,
  Divider,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FieldArray } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatCep, formatCpfCnpj } from '../../../utils';
import PhoneInput from '../../../components/PhoneInput';
import { tokens } from '../../../theme';

import { Button as CustomButton } from '../../../components/Button';
import DocumentUploadSection from '../../../components/DocumentUploadSection';

const taxRegimes = [
  { value: 'simples', label: 'Simples Nacional' },
  { value: 'lucro_presumido', label: 'Lucro Presumido' },
  { value: 'lucro_real', label: 'Lucro Real' },
];

const states = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
];

export const isCnpj = (value) => !!value && value.replace(/\D/g, '').length === 14;

export const DOCUMENT_TYPES = [
  {
    key: 'addressProof',
    fieldName: 'address_proof',
    label: 'Comprovante de endereço',
    hint: 'Envie um arquivo (PDF, JPG, PNG)',
  },
  {
    key: 'billingStatement',
    fieldName: 'billing_statement',
    label: 'Faturamento dos últimos 12 meses',
    hint: 'Envie um arquivo (PDF, JPG, PNG)',
  },
  {
    key: 'socialContract',
    fieldName: 'social_contract',
    label: 'Contrato social ou estatuto social',
    hint: 'Envie um arquivo (PDF, JPG, PNG)',
  },
];

const FormField = ({ label, required, children }) => (
  <Box>
    {label && (
      <Typography variant="body2" fontSize={14} mb={0.5} fontWeight={500} color="#374151">
        {label}
        {required && <span style={{ color: 'red', marginLeft: 2 }}>*</span>}
      </Typography>
    )}
    {children}
  </Box>
);

const RegisterTab = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  setFieldValue,
  loading,
  expandedCredit,
  setExpandedCredit,
  loadingCep,
  fetchAddressByCep,
  documentsByType,
  onSetDocument,
  onClearDocument,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  return (
    <form onSubmit={handleSubmit}>
      <Box mb={4} sx={{ border: '1px solid #E5E7EB', padding: 3, borderRadius: '8px' }}>
        <Typography
          variant="subtitle1"
          sx={{ fontSize: '18px', fontWeight: 700, mb: 2.5, color: colors.grey[200] }}
          color="#1f2937"
        >
          {t('forms.clientData')}
        </Typography>

        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
          <FormField label={t('forms.fullName')} required>
            <TextField
              fullWidth
              size="small"
              name="fullName"
              placeholder="João da Silva"
              value={values.fullName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.fullName && Boolean(errors.fullName)}
              helperText={touched.fullName && errors.fullName}
            />
          </FormField>

          <FormField label={t('forms.email')} required>
            <TextField
              fullWidth
              size="small"
              name="email"
              type="email"
              placeholder="joao.silva@email.com"
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.email && Boolean(errors.email)}
              helperText={touched.email && errors.email}
            />
          </FormField>
        </Box>

        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
          <FormField label={t('forms.phone')} required>
            <PhoneInput
              name="phone"
              value={values.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.phone && Boolean(errors.phone)}
              helperText={touched.phone && errors.phone}
            />
          </FormField>

          <FormField label={t('forms.cpfCnpj')} required>
            <TextField
              fullWidth
              size="small"
              name="taxvar"
              placeholder="123.456.789-00"
              value={formatCpfCnpj(values.taxvar)}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, '');
                handleChange({ target: { name: 'taxvar', value: cleaned } });
              }}
              onBlur={handleBlur}
              inputProps={{ maxLength: 18 }}
              error={touched.taxvar && Boolean(errors.taxvar)}
              helperText={touched.taxvar && errors.taxvar}
            />
          </FormField>
        </Box>
      </Box>

      {/* ── Endereço (visível para CPF e CNPJ) ──────────────────────────── */}
      <Box mb={4} sx={{ border: '1px solid #E5E7EB', padding: 3, borderRadius: '8px' }}>
        <Typography
          variant="subtitle1"
          sx={{ fontSize: '18px', fontWeight: 700, mb: 2.5, color: colors.grey[200] }}
        >
          {t('forms.addressCpfCnpj')}
        </Typography>

        {/* Linha 1: CEP | Estado | Cidade */}
        <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2} mb={2}>
          <FormField label={t('forms.zipCode')} required>
            <TextField
              fullWidth
              size="small"
              name="cep"
              placeholder="00000-000"
              value={formatCep(values.cep)}
              onChange={(e) => {
                const cleaned = e.target.value.replace(/\D/g, '');
                handleChange({ target: { name: 'cep', value: cleaned } });
              }}
              onBlur={(e) => {
                handleBlur(e);
                fetchAddressByCep(e.target.value, setFieldValue);
              }}
              inputProps={{ maxLength: 9 }}
              disabled={loadingCep}
              required
              error={touched.cep && Boolean(errors.cep)}
              helperText={loadingCep ? t('messages.fetchingCep') : touched.cep && errors.cep}
            />
          </FormField>

          <FormField label={t('forms.state')} required>
            <TextField
              fullWidth
              select
              size="small"
              name="state"
              value={values.state}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loadingCep}
              required
              error={touched.state && Boolean(errors.state)}
              helperText={touched.state && errors.state}
            >
              <MenuItem value="">{t('forms.select')}</MenuItem>
              {states.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </FormField>

          <FormField label={t('forms.city')} required>
            <TextField
              fullWidth
              size="small"
              name="city"
              placeholder={t('forms.city')}
              value={values.city}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loadingCep}
              required
              error={touched.city && Boolean(errors.city)}
              helperText={touched.city && errors.city}
            />
          </FormField>
        </Box>

        {/* Linha 2: Logradouro | Número | Complemento */}
        <Box display="grid" gridTemplateColumns="2fr 1fr 1fr" gap={2} mb={2}>
          <FormField label={t('forms.place')} required>
            <TextField
              fullWidth
              size="small"
              name="address"
              placeholder={t('forms.streetPlaceholder')}
              value={values.address}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loadingCep}
              required
              error={touched.address && Boolean(errors.address)}
              helperText={touched.address && errors.address}
            />
          </FormField>

          <FormField label={t('forms.number')} required>
            <TextField
              fullWidth
              size="small"
              name="number"
              placeholder={t('forms.numberPlaceholder')}
              value={values.number}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              error={touched.number && Boolean(errors.number)}
              helperText={touched.number && errors.number}
            />
          </FormField>

          <FormField label={t('forms.complement')}>
            <TextField
              fullWidth
              size="small"
              name="complement"
              placeholder={t('forms.complementPlaceholder')}
              value={values.complement}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FormField>
        </Box>

        {/* Linha 3: Bairro — preenchido via CEP */}
        <Box display="grid" gridTemplateColumns="1fr" gap={2}>
          <FormField label={t('forms.neighborhood')} required>
            <TextField
              fullWidth
              size="small"
              name="neighborhood"
              placeholder={t('forms.neighborhood')}
              value={values.neighborhood || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={loadingCep}
              required
              error={touched.neighborhood && Boolean(errors.neighborhood)}
              helperText={touched.neighborhood && errors.neighborhood}
            />
          </FormField>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box mb={4} sx={{ border: '1px solid #E5E7EB', padding: 3, borderRadius: '8px' }}>
        <Typography
          variant="subtitle1"
          sx={{ fontSize: '18px', fontWeight: 700, mb: 2.5, color: colors.grey[200] }}
        >
          Documentos
        </Typography>
        <Box display="flex" flexDirection="column" gap={1}>
          {DOCUMENT_TYPES.map(({ key, label, hint }) => (
            <DocumentUploadSection
              key={key}
              title={label}
              files={documentsByType[key] ? [documentsByType[key]] : []}
              onAddFiles={(files) => onSetDocument(key, files)}
              onRemoveFile={() => onClearDocument(key)}
              maxFiles={1}
              hint={hint}
            />
          ))}
        </Box>
      </Box>

      {/* ── Análise de crédito ────────────────────────────────────────────── */}
      {isCnpj(values.taxvar) && (
        <Box>
          <Button
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => setExpandedCredit((prev) => !prev)}
            sx={{
              textTransform: 'none',
              color: colors.greenAccent[500],
              fontWeight: 500,
              fontSize: 14,
              p: 0,
              '&:hover': { background: 'transparent', textDecoration: 'underline' },
            }}
          >
            {t('customers.fillInDataForAnalisy')}
          </Button>

          <Collapse in={expandedCredit}>
            <Box mt={3} sx={{ border: '1px solid #E5E7EB', padding: 3, borderRadius: '8px' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: colors.grey[200] }}>
                {t('titles.businessData')}
              </Typography>

              <Box mb={2}></Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                <FormField label={t('forms.companyName')}>
                  <TextField
                    fullWidth
                    size="small"
                    name="businessName"
                    placeholder={t('forms.companyName')}
                    value={values.businessName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </FormField>

                <FormField label={t('forms.municipalRegistration')}>
                  <TextField
                    fullWidth
                    size="small"
                    name="municipalRegistration"
                    placeholder="000000"
                    value={values.municipalRegistration}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </FormField>

                <FormField label={t('forms.stateRegistration')}>
                  <TextField
                    fullWidth
                    size="small"
                    name="stateRegistration"
                    placeholder="00000000000"
                    value={values.stateRegistration}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </FormField>

                <FormField label={t('forms.regimeType')}>
                  <TextField
                    fullWidth
                    select
                    size="small"
                    name="taxRegime"
                    value={values.taxRegime}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="">{t('forms.select')}</MenuItem>
                    {taxRegimes.map((r) => (
                      <MenuItem key={r.value} value={r.value}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormField>
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
                <FormField label={t('forms.financialEmail')}>
                  <TextField
                    fullWidth
                    size="small"
                    name="financialEmail"
                    type="email"
                    placeholder="email@email.com"
                    value={values.financialEmail}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </FormField>
                <FormField label={t('forms.phone')}>
                  <PhoneInput
                    name="financialPhone"
                    value={values.financialPhone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </FormField>
              </Box>

              {/* Sócios */}
              <Typography variant="h5" sx={{ fontWeight: 700, my: 2, color: colors.grey[200] }}>
                {t('forms.partners')}
              </Typography>

              <FieldArray name="partners">
                {({ push, remove }) => (
                  <>
                    {values.partners.map((partner, index) => (
                      <Box
                        key={index}
                        sx={{
                          mb: 3,
                          padding: 3,
                          backgroundColor: '#f7f9fa',
                          border: '1px solid #E5E7EB',
                          borderRadius: '8px',
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          mb={2}
                        >
                          <Typography variant="body2" fontWeight={600} fontSize={14}>
                            {t('forms.partner')} {index + 1}
                          </Typography>
                          {index > 0 && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => remove(index)}
                              sx={{ textTransform: 'none' }}
                            >
                              {t('buttons.remove')}
                            </Button>
                          )}
                        </Box>

                        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                          <FormField label={t('forms.fullName')}>
                            <TextField
                              fullWidth
                              size="small"
                              name={`partners.${index}.fullName`}
                              placeholder={t('forms.fullNamePlaceholder')}
                              value={partner.fullName}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.partners?.[index]?.fullName &&
                                Boolean(errors.partners?.[index]?.fullName)
                              }
                              helperText={
                                touched.partners?.[index]?.fullName &&
                                errors.partners?.[index]?.fullName
                              }
                              sx={{ backgroundColor: '#fff' }}
                            />
                          </FormField>

                          <FormField label={t('forms.email')}>
                            <TextField
                              fullWidth
                              size="small"
                              type="email"
                              placeholder={t('forms.emailPlaceholder')}
                              name={`partners.${index}.email`}
                              value={partner.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                touched.partners?.[index]?.email &&
                                Boolean(errors.partners?.[index]?.email)
                              }
                              helperText={
                                touched.partners?.[index]?.email &&
                                errors.partners?.[index]?.email
                              }
                              sx={{ backgroundColor: '#fff' }}
                            />
                          </FormField>

                          <FormField label={t('forms.cpf')}>
                            <TextField
                              fullWidth
                              size="small"
                              name={`partners.${index}.taxvar`}
                              placeholder="000.000.000-00"
                              value={formatCpfCnpj(partner.taxvar)}
                              onChange={(e) => {
                                setFieldValue(
                                  `partners.${index}.taxvar`,
                                  e.target.value.replace(/\D/g, '')
                                );
                              }}
                              inputProps={{ maxLength: 14 }}
                              sx={{ backgroundColor: '#fff' }}
                            />
                          </FormField>

                          <FormField label={t('forms.document')}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder="00.000.000-0"
                              name={`partners.${index}.document`}
                              value={partner.document}
                              onChange={handleChange}
                              sx={{ backgroundColor: '#fff' }}
                            />
                          </FormField>

                          <FormField label={t('forms.phone')}>
                            <PhoneInput
                              name={`partners.${index}.phone`}
                              value={partner.phone}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              sx={{ backgroundColor: '#fff' }}
                            />
                          </FormField>
                        </Box>
                      </Box>
                    ))}

                    <Button
                      startIcon={<AddIcon />}
                      onClick={() =>
                        push({ fullName: '', email: '', phone: '', taxvar: '', document: '' })
                      }
                      sx={{
                        color: '#4B5563',
                        textTransform: 'none',
                        px: 2,
                        fontWeight: 500,
                        border: '1px solid #D1D5DB',
                      }}
                    >
                      {t('forms.addPartner')}
                    </Button>
                  </>
                )}
              </FieldArray>
            </Box>
          </Collapse>
        </Box>
      )}

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <CustomButton
          type="submit"
          fullWidth={false}
          disabled={loading}
          sx={{ textTransform: 'uppercase' }}
        >
          {t('common.save')}
        </CustomButton>
      </Box>
    </form>
  );
};

export default RegisterTab;
